import { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Area,
  AreaChart,
} from 'recharts';
import { fetchRevenueChartData, type ChartDayData } from '@/lib/supabase';

type ChartMode = 'daily' | 'weekly';
type ChartType = 'bar' | 'area';

interface CustomTooltipProps {
  active?: boolean;
  payload?: { value: number; dataKey: string }[];
  label?: string;
  data: ChartDayData[];
}

function CustomTooltip({ active, payload, label, data }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) return null;
  const day = data.find(d => d.label === label);
  return (
    <div className="bg-white border border-gray-200 rounded-xl px-3 py-2.5 text-xs">
      <p className="text-gray-500 font-medium mb-1">{day?.date ?? label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="font-bold" style={{ color: p.dataKey === 'revenue' ? '#111827' : '#f59e0b' }}>
          {p.dataKey === 'revenue'
            ? `₹${p.value.toLocaleString('en-IN')}`
            : `${p.value} orders`}
        </p>
      ))}
    </div>
  );
}

export default function RevenueChart() {
  const [mode, setMode] = useState<ChartMode>('daily');
  const [chartType, setChartType] = useState<ChartType>('bar');
  const [data, setData] = useState<ChartDayData[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeMetric, setActiveMetric] = useState<'revenue' | 'orders'>('revenue');

  const load = async (m: ChartMode) => {
    setLoading(true);
    const result = await fetchRevenueChartData(m);
    setData(result);
    setLoading(false);
  };

  useEffect(() => { load(mode); }, [mode]);

  const totalRevenue = data.reduce((s, d) => s + d.revenue, 0);
  const totalOrders = data.reduce((s, d) => s + d.orders, 0);
  const maxRevenue = Math.max(...data.map(d => d.revenue), 1);
  const peakDay = data.reduce((best, d) => d.revenue > best.revenue ? d : best, data[0] ?? { label: '-', revenue: 0, orders: 0, date: '' });

  const chartColor = activeMetric === 'revenue' ? '#111827' : '#f59e0b';
  const chartColorLight = activeMetric === 'revenue' ? '#e5e7eb' : '#fef3c7';

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-gray-900 flex items-center justify-center shrink-0">
            <i className="ri-line-chart-line text-white text-base"></i>
          </div>
          <div>
            <p className="text-gray-900 font-bold text-sm leading-none">Revenue Chart</p>
            <p className="text-gray-400 text-xs mt-0.5">
              {mode === 'daily' ? 'Last 7 days' : 'Last 4 weeks'}
            </p>
          </div>
        </div>

        {/* Chart type toggle */}
        <div className="flex items-center bg-gray-100 rounded-xl p-1 gap-1">
          <button
            onClick={() => setChartType('bar')}
            className={`w-8 h-7 rounded-lg flex items-center justify-center text-xs transition-all cursor-pointer ${
              chartType === 'bar' ? 'bg-white text-gray-900' : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            <i className="ri-bar-chart-2-line"></i>
          </button>
          <button
            onClick={() => setChartType('area')}
            className={`w-8 h-7 rounded-lg flex items-center justify-center text-xs transition-all cursor-pointer ${
              chartType === 'area' ? 'bg-white text-gray-900' : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            <i className="ri-line-chart-line"></i>
          </button>
        </div>
      </div>

      {/* Mode Tabs */}
      <div className="flex items-center bg-gray-100 rounded-xl p-1 mb-4 gap-1">
        <button
          onClick={() => setMode('daily')}
          className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
            mode === 'daily' ? 'bg-white text-gray-900' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <i className="ri-calendar-line mr-1"></i>Daily (7 Days)
        </button>
        <button
          onClick={() => setMode('weekly')}
          className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
            mode === 'weekly' ? 'bg-white text-gray-900' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <i className="ri-calendar-2-line mr-1"></i>Weekly (4 Weeks)
        </button>
      </div>

      {/* Metric Toggle */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => setActiveMetric('revenue')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
            activeMetric === 'revenue'
              ? 'bg-gray-900 text-white'
              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
        >
          <i className="ri-money-rupee-circle-line"></i>Revenue
        </button>
        <button
          onClick={() => setActiveMetric('orders')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
            activeMetric === 'orders'
              ? 'bg-amber-500 text-white'
              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
        >
          <i className="ri-shopping-bag-line"></i>Orders
        </button>
      </div>

      {/* Summary strips */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-gray-50 rounded-xl p-2.5 text-center">
          <p className="text-gray-900 font-bold text-sm leading-none">
            ₹{totalRevenue.toLocaleString('en-IN')}
          </p>
          <p className="text-gray-400 text-[10px] mt-0.5">Total Revenue</p>
        </div>
        <div className="bg-amber-50 rounded-xl p-2.5 text-center">
          <p className="text-amber-700 font-bold text-sm leading-none">{totalOrders}</p>
          <p className="text-gray-400 text-[10px] mt-0.5">Total Orders</p>
        </div>
        <div className="bg-green-50 rounded-xl p-2.5 text-center">
          <p className="text-green-700 font-bold text-sm leading-none truncate">
            {peakDay?.label ?? '-'}
          </p>
          <p className="text-gray-400 text-[10px] mt-0.5">Peak Day</p>
        </div>
      </div>

      {/* Chart */}
      {loading ? (
        <div className="flex flex-col items-center justify-center h-44 gap-3">
          <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-gray-900 animate-spin"></div>
          <p className="text-gray-400 text-xs">Chart load ho rahi hai...</p>
        </div>
      ) : totalRevenue === 0 && totalOrders === 0 ? (
        <div className="flex flex-col items-center justify-center h-44 gap-3 bg-gray-50 rounded-xl">
          <div className="w-12 h-12 rounded-2xl bg-white border border-gray-100 flex items-center justify-center">
            <i className="ri-bar-chart-line text-2xl text-gray-300"></i>
          </div>
          <div className="text-center">
            <p className="text-gray-500 text-sm font-semibold">Koi data nahi</p>
            <p className="text-gray-400 text-xs mt-0.5">
              {mode === 'daily' ? 'Pichhle 7 din' : 'Pichhle 4 hafte'} mein koi delivered order nahi
            </p>
          </div>
        </div>
      ) : (
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            {chartType === 'bar' ? (
              <BarChart data={data} barSize={mode === 'daily' ? 28 : 40} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => activeMetric === 'revenue' ? (v >= 1000 ? `₹${(v/1000).toFixed(0)}k` : `₹${v}`) : `${v}`}
                />
                <Tooltip content={<CustomTooltip data={data} />} cursor={{ fill: '#f9fafb' }} />
                <Bar
                  dataKey={activeMetric}
                  fill={chartColor}
                  radius={[6, 6, 0, 0]}
                />
              </BarChart>
            ) : (
              <AreaChart data={data} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorMetric" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={chartColor} stopOpacity={0.15} />
                    <stop offset="95%" stopColor={chartColor} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => activeMetric === 'revenue' ? (v >= 1000 ? `₹${(v/1000).toFixed(0)}k` : `₹${v}`) : `${v}`}
                />
                <Tooltip content={<CustomTooltip data={data} />} />
                <Area
                  type="monotone"
                  dataKey={activeMetric}
                  stroke={chartColor}
                  strokeWidth={2.5}
                  fill="url(#colorMetric)"
                  dot={{ fill: chartColor, r: 4, strokeWidth: 0 }}
                  activeDot={{ r: 6, fill: chartColor, strokeWidth: 0 }}
                />
              </AreaChart>
            )}
          </ResponsiveContainer>
        </div>
      )}

      {/* Mini day list */}
      {!loading && data.length > 0 && (totalRevenue > 0 || totalOrders > 0) && (
        <div className="mt-4 space-y-1.5">
          {data.map((d) => {
            const barPct = activeMetric === 'revenue'
              ? Math.round((d.revenue / maxRevenue) * 100)
              : Math.round((d.orders / Math.max(...data.map(x => x.orders), 1)) * 100);
            const isToday = mode === 'daily' && d === data[data.length - 1];
            return (
              <div key={d.label} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${isToday ? 'bg-gray-50' : ''}`}>
                <p className={`text-[10px] font-bold w-16 shrink-0 ${isToday ? 'text-gray-900' : 'text-gray-400'}`}>
                  {d.date}
                  {isToday && <span className="ml-1 text-green-500">●</span>}
                </p>
                <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${barPct}%`, backgroundColor: chartColor }}
                  ></div>
                </div>
                <p className={`text-[10px] font-bold w-16 text-right shrink-0 ${activeMetric === 'revenue' ? 'text-gray-700' : 'text-amber-600'}`}>
                  {activeMetric === 'revenue'
                    ? `₹${d.revenue.toLocaleString('en-IN')}`
                    : `${d.orders} orders`}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
